import './Assistant.css';

import {
  Box,
  Button,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import AdvancedInstructions from 'pages/ScribeDashboard/MiddlePanel/components/AdvancedInstructions';
import React from 'react';
import * as Yup from 'yup';

// @ts-ignore
import RichTextEditor from '../ScribeDashboard/MiddlePanel/components/RichTextEditor';

const formSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  category: Yup.string().required('Category is required'),
  language: Yup.string().required('Language is required'),
  advanceInstructions: Yup.string().required(
    'Advance Instructions is required'
  ),
  example1: Yup.string().required('Document 1 is required'),
  example2: Yup.string().required('Document 2 is required'),
});

const LibraryEditForm = ({
  closeForm,
  selectedItem,
  updateGlobalData,
}: {
  closeForm: () => void;
  selectedItem: any;
  updateGlobalData: (newGlobalData: any) => void;
}) => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const userRolesData = localStorage.getItem('roles') || '';
  const rolesData = userRolesData ? JSON.parse(userRolesData) : null;

  const oauthToken = localStorage.getItem('AUTH_TOKEN') || '';

  if (!oauthToken) {
    throw new Error('OAuth token is missing');
  }

  const languageOptions = [
    { value: 'english', display: 'English' },
    { value: 'german', display: 'German' },
    { value: 'russian', display: 'Russian' },
    { value: 'italian', display: 'Italian' },
    { value: 'polish', display: 'Polish' },
    { value: 'ukrainian', display: 'Ukrainian' },
    { value: 'french', display: 'French' },
    { value: 'spanish', display: 'Spanish' },
  ];

  interface Examples {
    example1: string;
    example2: string;
  }

  const generateInstructionString = (
    category: string,
    language: string,
    examples: Examples,
    instruction = ''
  ) => {
    if (!category) return '';

    // Base instruction that's common for all types
    let baseString = 'You are a medical transcription assistant. ';

    // Add category-specific instructions
    switch (category.toLowerCase()) {
      case 'report':
        baseString +=
          'you will generate a medical report, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      case 'procedure':
        baseString +=
          'you will generate a medical procedure note, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      case 'letter':
        baseString +=
          'you will generate a medical letter, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary unless otherwise specified. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      case 'custom':
        baseString +=
          'you will generate a medical custom, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      default:
        console.warn(`Unexpected category: ${category}`);
        break;
    }

    // Add language specification
    const selectedLanguage = languageOptions.find(
      (lang) => lang.value === language
    )?.display;
    if (!selectedLanguage) {
      console.warn(
        `Language not found for value: ${language}, defaulting to English`
      );
    }
    baseString += `\n\n### Output language must be ${
      selectedLanguage || 'English'
    }`;

    // Add advanced instructions if present
    if (instruction && instruction.trim()) {
      baseString += `\n\n### Advanced Instructions:\n${instruction.trim()}`;
    }

    // Add standard instruction about ignoring casual dialogue
    baseString +=
      '\n\n###ignore casual or non-medical dialogue. Focus on organizing the relevant information into a medical note. ' +
      'If some parts of the input are unclear, process as much as possible based on the available information. ' +
      'Process the text as-is, treating interruptions or incomplete sentences as part of the conversation. ' +
      'Extract meaningful content for a medical note.';

    // Add examples - handle both object and direct example1/example2 format
    if (examples.example1 || examples.example2) {
      if (examples.example1?.trim()) {
        baseString += `\n\nExample 1:\n${examples.example1.trim()}`;
      }
      if (examples.example2?.trim()) {
        baseString += `\n\nExample 2:\n${examples.example2.trim()}`;
      }
    } else if (typeof examples === 'object') {
      // If examples is passed directly as { example1, example2 }
      baseString += `\n\nExample 1:\n${(examples.example1 || '').trim()}`;
      baseString += `\n\nExample 2:\n${(examples.example2 || '').trim()}`;
    }

    return baseString;
  };

  const copyToClipboard = (data: any) => {
    const textToCopy = JSON.stringify(data, null, 2); // You can format the data as needed
    navigator.clipboard.writeText(textToCopy);
  };

  const handleApiCall = async (values: any) => {
    try {
      const instructionString = generateInstructionString(
        values.category,
        values.language,
        {
          example1: values.example1,
          example2: values.example2,
        },
        values.advanceInstructions
      );

      const combinedData = {
        formData: values,
        instructionString,
      };
      // First API call - Save instruction
      const response = await fetch(`${baseURL}/api/assistant-instructions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${oauthToken}`, // Adding the Bearer token to the headers
        },
        body: JSON.stringify({
          userid: rolesData?.id,
          account_id: rolesData?.account?.id,
          name: values.name,
          category: values.category,
          status: 'Active',
          language: values.language,
          advanced_instructions: values.advanceInstructions,
          example1: values.example1 || '',
          example2: values.example2 || '',
          instruction: instructionString,
        }),
      });

      if (!response.ok) {
        const errorMessage = await response.text(); // or parseErrorResponse if you have a separate error function
        throw new Error(errorMessage);
      }

      // Alert on successful save
      alert('Instruction saved successfully');
      closeForm(); // Close the form on successful submission
      copyToClipboard(combinedData);
      // Second API call - Get global instructions
      const globalResponse = await fetch(`${baseURL}/api/instructions/global`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${oauthToken}`, // Adding the Bearer token to the headers
        },
      });

      if (!globalResponse.ok) {
        const globalErrorMessage = await globalResponse.text();
        throw new Error(globalErrorMessage);
      }

      // Handle global response, if needed
      const globalData = await globalResponse.json();

      updateGlobalData(globalData);
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <Formik
      initialValues={{
        name: selectedItem ? selectedItem.name : '',
        category: selectedItem ? selectedItem.category : '',
        language: selectedItem ? selectedItem.language : '',
        advanceInstructions: selectedItem
          ? selectedItem?.advanced_instructions
          : '',
        example1: selectedItem ? selectedItem.example1 : '',
        example2: selectedItem ? selectedItem.example2 : '',
      }}
      validationSchema={formSchema}
      onSubmit={handleApiCall}
    >
      {({ errors, touched, values, setFieldValue }: any) => (
        <Form className="mx-auto mt-2 w-[775px] space-y-6 rounded-md p-3 px-0 pl-1">
          <div className="flex h-[calc(100vh-170px)] flex-col gap-3 overflow-auto pr-2">
            {/* Name Input */}
            <Box>
              <FormLabel htmlFor="name" sx={{ fontSize: '0.9rem' }}>
                Name
              </FormLabel>
              <Field
                id="name"
                name="name"
                as={TextField}
                fullWidth
                variant="outlined"
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                InputProps={{ sx: { fontSize: '0.9rem' } }}
                placeholder="Enter a name for this instruction set"
              />
            </Box>

            {/* Category Dropdown */}
            <Box>
              <FormLabel htmlFor="category" sx={{ fontSize: '0.9rem' }}>
                Category
              </FormLabel>
              <Field
                as={Select}
                id="category"
                name="category"
                fullWidth
                variant="outlined"
                error={touched.category && !!errors.category}
                displayEmpty
                value={values.category}
                onChange={(e: any) => setFieldValue('category', e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select Category
                </MenuItem>
                <MenuItem value="report">Report</MenuItem>
                <MenuItem value="procedure">Procedure</MenuItem>
                <MenuItem value="letter">Letter</MenuItem>
                <MenuItem value="Custom">Custom Note</MenuItem>
              </Field>
              {touched.category && errors.category && (
                <div style={{ color: 'red', fontSize: '0.75rem' }}>
                  {errors.category}
                </div>
              )}
            </Box>

            {/* Language Dropdown */}
            <Box>
              <FormLabel htmlFor="language" sx={{ fontSize: '0.9rem' }}>
                Language
              </FormLabel>
              <Field
                as={Select}
                id="language"
                name="language"
                fullWidth
                variant="outlined"
                error={touched.language && !!errors.language}
                displayEmpty
                value={values.language}
                onChange={(e: any) => setFieldValue('language', e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select Language
                </MenuItem>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="german">German</MenuItem>
                <MenuItem value="russian">Russian</MenuItem>
                <MenuItem value="italian">Italian</MenuItem>
                <MenuItem value="polish">Polish</MenuItem>
                <MenuItem value="ukrainian">Ukrainian</MenuItem>
                <MenuItem value="french">French</MenuItem>
                <MenuItem value="spanish">Spanish</MenuItem>
              </Field>
              {touched.language && errors.language && (
                <div style={{ color: 'red', fontSize: '0.75rem' }}>
                  {errors.language}
                </div>
              )}
            </Box>

            {/* Advance Instructions */}
            <Box>
              <FormLabel
                htmlFor="advanceInstructions"
                sx={{ fontSize: '0.9rem' }}
              >
                Advanced Instructions
              </FormLabel>
              <AdvancedInstructions
                value={selectedItem ? selectedItem.advanced_instructions : ''}
                onChange={(data: { instructionsText: string; json: any }) => {
                  setFieldValue('advanceInstructions', data.instructionsText);
                }}
              />
            </Box>

            {/* Document 1 */}
            <Box>
              <FormLabel htmlFor="example1" sx={{ fontSize: '0.9rem' }}>
                Sample Document 1
              </FormLabel>
              <RichTextEditor
                value={values.example1}
                onChange={(value: any) => setFieldValue('example1', value)}
                setActiveTextArea={() => {}}
              />
              {touched.example1 && errors.example1 && (
                <div
                  style={{
                    color: '#F04438',
                    fontSize: '0.75rem',
                    marginLeft: '14px',
                  }}
                >
                  {errors.example1}
                </div>
              )}
            </Box>

            {/* Document 2 */}
            <Box>
              <FormLabel htmlFor="example2" sx={{ fontSize: '0.9rem' }}>
                Sample Document 2
              </FormLabel>
              <RichTextEditor
                value={values.example2}
                onChange={(value: any) => setFieldValue('example2', value)}
                setActiveTextArea={() => {}}
              />
              {touched.example2 && errors.example2 && (
                <div
                  style={{
                    color: '#F04438',
                    fontSize: '0.75rem',
                    marginLeft: '14px',
                  }}
                >
                  {errors.example2}
                </div>
              )}
            </Box>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between pr-2">
            <Button
              variant="outlined"
              type="button"
              color="secondary"
              className="w-[48%] py-2"
              onClick={closeForm}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="w-[48%] py-2"
            >
              Save Assistant
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LibraryEditForm;
